import React, { useState, useRef, useEffect } from "react";
import Resizer from "react-image-file-resizer";
import { Button } from "./ui/button";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import type { ProgressSectionId } from "../lib/progress-calculator";
import { getNamespaceTranslations } from "../lib/questionnaire-translations.ts";

const hasGetUserMedia = () =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia;

/**
 * Parses JSON API error bodies (and plain text) into one readable line for the UI.
 */
function humanizeUploadErrorBody(body: string, httpStatus: number): string {
  const trimmed = body.trim();
  if (!trimmed) {
    return httpStatus ? `Upload failed (${httpStatus})` : "Upload failed";
  }

  const looksLikeJson =
    trimmed.startsWith("{") || trimmed.startsWith("[");

  if (!looksLikeJson) {
    return trimmed;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object") {
      return trimmed;
    }
    const o = parsed as Record<string, unknown>;
    const parts: string[] = [];

    const push = (value: unknown) => {
      if (typeof value === "string" && value.trim()) {
        parts.push(value.trim());
      }
    };

    push(o.message);
    push(o.error);
    push(o.detail);

    const errs = o.errors;
    if (errs && typeof errs === "object" && !Array.isArray(errs)) {
      const errObj = errs as Record<string, unknown>;
      push(errObj.message);
      for (const v of Object.values(errObj)) {
        if (typeof v === "string") {
          push(v);
        } else if (Array.isArray(v)) {
          for (const item of v) {
            push(item);
          }
        }
      }
    } else if (Array.isArray(errs)) {
      for (const e of errs) {
        if (typeof e === "string") {
          push(e);
        } else if (e && typeof e === "object") {
          push((e as { message?: unknown }).message);
        }
      }
    }

    if (Array.isArray(o.graphQLErrors)) {
      for (const e of o.graphQLErrors) {
        if (e && typeof e === "object") {
          push((e as { message?: unknown }).message);
        }
      }
    }

    const unique = [...new Set(parts)];
    if (unique.length === 0) {
      return trimmed;
    }
    if (unique.length === 1) {
      return unique[0];
    }
    return unique.join(" — ");
  } catch {
    return trimmed;
  }
}

interface FileUploadProps {
  patientId: string;
  caseId: string;
  shopifyCustomerId?: string;
  email?: string;
  productUrl?: string;
  customerAccessToken?: string | null;
  onSuccess: (response: any) => void | Promise<void>;
  onBack?: () => void;
  onClose?: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
  fileUploadGuideTitle?: string;
  initialFile?: File | null;
  onFileChange?: (file: File | null) => void;
}

export function FileUpload({
  patientId,
  caseId,
  shopifyCustomerId,
  email,
  productUrl,
  customerAccessToken,
  onSuccess,
  onBack,
  onClose,
  language = "en",
  logoUrl,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  fileUploadGuideTitle,
  initialFile = null,
  onFileChange,
}: FileUploadProps) {
  const t = getNamespaceTranslations(language, "fileUpload")!;

  const [file, setFile] = useState<File | null>(initialFile);
  const [error, setError] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Stays true after upload until page navigates
  const [isCompressing, setIsCompressing] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const maxSizeMB = 25; // 25 MB max input size
  const maxUploadSizeMB = 4; // 4 MB max for Lambda (with some buffer)

  // Compress image to fit within Lambda's size limit
  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      // Determine output format (keep original or convert to JPEG for better compression)
      const outputFormat = file.type === "image/png" ? "PNG" : "JPEG";

      // Start with high quality, we'll reduce if needed
      let quality = 90;
      let maxWidth = 2048;
      let maxHeight = 2048;

      const attemptCompress = () => {
        Resizer.imageFileResizer(
          file,
          maxWidth,
          maxHeight,
          outputFormat,
          quality,
          0, // rotation
          (result) => {
            const compressedFile = result as File;

            // Check if we're under the limit
            if (compressedFile.size <= maxUploadSizeMB * 1024 * 1024) {
              resolve(compressedFile);
            } else if (quality > 30) {
              // Reduce quality and try again
              quality -= 20;
              attemptCompress();
            } else if (maxWidth > 1024) {
              // Reduce dimensions and try again
              quality = 70;
              maxWidth = 1024;
              maxHeight = 1024;
              attemptCompress();
            } else {
              // Give up - file is still too large
              reject(new Error("Unable to compress image to required size"));
            }
          },
          "file" // output type
        );
      };

      attemptCompress();
    });
  };

  const startCamera = async () => {
    if (!hasGetUserMedia()) {
      setCameraError(t.cameraError);
      return;
    }
    setCameraError(null);
    setShowCamera(true);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCameraError(null);
  };

  useEffect(() => {
    if (!showCamera || !videoRef.current) return;
    const video = videoRef.current;
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      .then((stream) => {
        streamRef.current = stream;
        video.srcObject = stream;
        video.play().catch(() => {});
      })
      .catch(() => {
        setCameraError(t.cameraError);
      });
    return stopCamera;
  }, [showCamera, t.cameraError]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Lift the selected file to the parent so it survives this screen unmounting/remounting
  // (e.g. navigating away to shipping-info and back) — see onFileChange/initialFile above.
  useEffect(() => {
    onFileChange?.(file);
  }, [file, onFileChange]);

  useEffect(() => {
    if (!file) setShowPreview(false);
  }, [file]);

  useEffect(() => {
    if (!showPreview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowPreview(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showPreview]);

  const clearFile = () => {
    setFile(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const captureFromCamera = async () => {
    const video = videoRef.current;
    if (!video || !video.srcObject || video.readyState < 2) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const f = new File([blob], "capture.jpg", { type: "image/jpeg" });
        if (f.size > maxUploadSizeMB * 1024 * 1024) {
          setIsCompressing(true);
          compressImage(f)
            .then((compressed) => {
              setFile(compressed);
              stopCamera();
            })
            .catch(() => setCameraError(t.compressionError || ""))
            .finally(() => setIsCompressing(false));
        } else {
          setFile(f);
          stopCamera();
        }
      },
      "image/jpeg",
      0.9
    );
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setError("");
    setFile(null);

    if (!f) return;

    // Validate file type
    if (!allowedTypes.includes(f.type)) {
      setError(t.includes);
      return;
    }

    // Validate initial file size (before compression)
    if (f.size > maxSizeMB * 1024 * 1024) {
      setError(t.fileSizeError + ` ${maxSizeMB}MB.`);
      return;
    }

    // Compress if file is larger than Lambda limit
    if (f.size > maxUploadSizeMB * 1024 * 1024) {
      setIsCompressing(true);
      try {
        const compressedFile = await compressImage(f);
        setFile(compressedFile);
      } catch {
        setError(t.compressionError || "Unable to compress image. Please try a smaller file.");
      } finally {
        setIsCompressing(false);
      }
    } else {
      // File is already small enough, use as-is
      setFile(f);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError(t.noFile);
      return;
    }

    // By this point in the flow the Shopify account was already created back at account-details'
    // access step, so a missing token here means something went wrong upstream — block rather
    // than silently sending an empty token the backend will reject anyway.
    if (!customerAccessToken) {
      setError(
        language === "es"
          ? "No se pudo verificar tu cuenta. Por favor, actualiza la página e inténtalo de nuevo."
          : "We could not verify your account. Please refresh the page and try again."
      );
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("patient_id", patientId);
    formData.append("case_id", caseId);
    formData.append("email", email || "");
    formData.append("product_url", productUrl || window.location.href.split('?')[0]);
    formData.append("customer_access_token", customerAccessToken);
    if (shopifyCustomerId) {
      formData.append("shopify_customer_id", shopifyCustomerId);
    }

    try {
      setIsUploading(true);
      setError("");
      const res = await fetch("/apps/rx/file", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        if (text.includes("<html")) {
          // had a server error that resulted in returned HTML
          // so as not to show that to the user, we replace with the generic message
          // added the [500] to help identify server errors in logs/screenshots
          throw new Error(`${t.uploadFailed} [500]`);
        }
        const readable = humanizeUploadErrorBody(
          text,
          res.status,
        );
        throw new Error(readable || `Upload failed with status ${res.status}`);
      }
      const json = await res.json().catch(() => ({}));
      // Set processing state BEFORE calling onSuccess - this keeps button disabled
      // until page navigates away to checkout
      setIsProcessing(true);
      setIsUploading(false);
      try {
        await onSuccess(json);
      } catch (err: any) {
        // Add-to-cart / finish checkout failed — stay on this step with an error
        setIsProcessing(false);
        const raw = typeof err?.message === "string" ? err.message : "";
        setError(raw || t.uploadFailed);
      }
    } catch (err: any) {
      console.error("[FileUpload] Upload error", err);
      const raw = typeof err?.message === "string" ? err.message : "";
      const looksLikeJson =
        raw.trim().startsWith("{") || raw.trim().startsWith("[");
      setError(
        looksLikeJson
          ? humanizeUploadErrorBody(raw, 0) || t.uploadFailed
          : raw || t.uploadFailed,
      );
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header logoUrl={logoUrl} onBack={onBack} onClose={onClose} />

      <div className="flex-1 flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
        {/* Progress Bar */}
        <div className="px-4 sm:px-6 pt-[30px]">
          <div className="max-w-[640px] mx-auto">
            {currentStep !== undefined &&
              totalSteps !== undefined &&
              sectionIndex !== undefined &&
              totalSections !== undefined &&
              sectionId !== undefined && (
              <ProgressBar
                currentStep={currentStep}
                totalSteps={totalSteps}
                sectionIndex={sectionIndex}
                totalSections={totalSections}
                sectionId={sectionId}
                language={language}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 pt-8 pb-[30px]">
          <div className="max-w-[640px] mx-auto">
            {/* Title */}
            <div className="mb-6">
              <h2
                className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal text-black mb-2"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {t.title}
              </h2>
              <p
                id="file-description"
                className="text-base leading-[22px] text-gray-500"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {t.acceptedFormats}: JPG, PNG, WEBP. {t.maxFileSize}: {maxSizeMB}MB.
              </p>
            </div>

            {/* File Upload Area */}
            <div className="mb-[30px]">
              <label
                htmlFor="file"
                className="block text-sm font-medium text-black mb-2 required-label"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {t.label}
              </label>

              {/* Hidden file inputs - one for gallery, one for camera */}
              <input
                ref={fileInputRef}
                id="file"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
                disabled={isUploading || isCompressing}
                aria-describedby={error ? "file-error file-description" : "file-description"}
                aria-invalid={!!error}
                aria-required="true"
                className="sr-only"
                data-testid="fileupload_input-file"
              />
              {/* Custom file input buttons */}
              <div className="flex flex-wrap items-center gap-3 w-full px-4 py-3 border border-gray-300 rounded-lg bg-white">
                <label
                  htmlFor="file"
                  className={`inline-flex items-center px-4 py-2 rounded-full border-0 text-sm font-semibold bg-[#2D55FB] text-white hover:bg-[#2347E8] cursor-pointer transition-colors ${
                    isUploading || isCompressing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  style={{ fontFamily: "TT Commons Pro" }}
                  tabIndex={isUploading || isCompressing ? -1 : 0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      document.getElementById('file')?.click();
                    }
                  }}
                  data-testid="fileupload-choose_file"
                >
                  {t.chooseFile}
                </label>
                <button
                  type="button"
                  onClick={startCamera}
                  disabled={isUploading || isCompressing}
                  className={`inline-flex items-center px-4 py-2 rounded-full border border-gray-300 text-sm font-semibold bg-white text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors ${
                    isUploading || isCompressing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  style={{ fontFamily: "TT Commons Pro" }}
                  data-testid="fileupload-take_photo"
                >
                  {t.takePhoto}
                </button>
                <span
                  className={`text-sm font-medium ${file ? "text-gray-900" : "text-gray-600"}`}
                  style={{ fontFamily: "TT Commons Pro" }}
                >
                  {isCompressing ? t.compressing : file ? file.name : t.noFileChosen}
                </span>
              </div>

              {file && previewUrl && (
                <div
                  className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4"
                  role="status"
                  aria-live="polite"
                  data-testid="fileupload-file-ready"
                >
                  <p
                    className="text-base font-semibold text-emerald-900"
                    style={{ fontFamily: "TT Commons Pro" }}
                  >
                    {t.fileUploaded}
                  </p>
                  <p
                    className="mt-1 text-sm font-medium text-gray-900 break-all"
                    style={{ fontFamily: "TT Commons Pro" }}
                  >
                    {file.name}
                  </p>
                  <div className="mt-4 flex flex-wrap items-start gap-3">
                    <img
                      src={previewUrl}
                      alt=""
                      className="h-28 max-w-[min(100%,280px)] rounded-md border border-emerald-200/80 object-contain bg-white"
                    />
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <button
                        type="button"
                        onClick={() => setShowPreview(true)}
                        className="inline-flex items-center justify-center rounded-full border border-emerald-700 bg-white px-4 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-100"
                        style={{ fontFamily: "TT Commons Pro" }}
                        data-testid="fileupload-preview"
                      >
                        {t.preview}
                      </button>
                      <button
                        type="button"
                        onClick={clearFile}
                        className="inline-flex items-center justify-center rounded-full border border-gray-400 bg-white px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                        style={{ fontFamily: "TT Commons Pro" }}
                        data-testid="fileupload-remove"
                      >
                        {t.removeFile}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div
                id="file-error"
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                role="alert"
                aria-live="assertive"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {error}
              </div>
            )}

            {/* Full-size preview modal */}
            {showPreview && previewUrl && file && (
              <div
                className="fixed inset-0 z-[60] flex flex-col bg-black/90 p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="preview-title"
                onClick={() => setShowPreview(false)}
              >
                <div
                  className="flex min-h-0 flex-1 flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex shrink-0 items-center justify-between gap-4 pb-3">
                    <h3
                      id="preview-title"
                      className="text-base font-semibold text-white truncate pr-2"
                      style={{ fontFamily: "TT Commons Pro" }}
                    >
                      {t.preview}: {file.name}
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowPreview(false)}
                      className="shrink-0 !border-white !bg-transparent !text-white hover:!bg-white/20 hover:!text-white focus-visible:ring-white focus-visible:ring-offset-black"
                      data-testid="fileupload-preview-close"
                    >
                      {t.closePreview}
                    </Button>
                  </div>
                  <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto">
                    <img
                      src={previewUrl}
                      alt=""
                      className="max-h-[min(85vh,100%)] max-w-full object-contain"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Camera capture modal */}
            {showCamera && (
              <div
                className="fixed inset-0 z-50 flex flex-col bg-black"
                role="dialog"
                aria-modal="true"
                aria-labelledby="camera-title"
              >
                <div className="flex-1 flex flex-col items-center justify-center p-4">
                  <h3 id="camera-title" className="sr-only">
                    {t.takePhoto}
                  </h3>
                  {cameraError ? (
                    <p className="text-white text-center mb-4">{cameraError}</p>
                  ) : (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="max-w-full max-h-[60vh] object-contain rounded-lg bg-black"
                    />
                  )}
                </div>
                <div className="flex gap-3 p-4 bg-black/80">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={stopCamera}
                    className="flex-1 !border-white !bg-transparent !text-white hover:!bg-white/20 hover:!text-white focus-visible:ring-white focus-visible:ring-offset-black"
                  >
                    {t.cancelCamera}
                  </Button>
                  {!cameraError && (
                    <Button
                      type="button"
                      onClick={captureFromCamera}
                      disabled={isCompressing}
                      className="flex-1 bg-[#2D55FB] hover:bg-[#2347E8] text-white"
                    >
                      {isCompressing ? t.compressing : t.capturePhoto}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* File upload guide button - Only show if fileUploadGuideTitle exists */}
            {fileUploadGuideTitle && (
              <button
                type="button"
                data-fileupload-modal-trigger
                aria-label={fileUploadGuideTitle}
                data-testid="fileupload-guide"
                className="w-full flex items-center justify-between p-4 border border-[#E0E0E0] bg-[#F7F1F1] rounded-[6px] mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D55FB] focus-visible:ring-offset-2"
              >
                <span
                  className="text-black text-base leading-6 font-normal"
                  style={{ fontFamily: 'TT Commons Pro' }}
                >
                  {fileUploadGuideTitle}
                </span>
                <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <path d="M8.375 1.5C6.03125 1.5 3.90625 2.75 2.71875 4.75C1.5625 6.78125 1.5625 9.25 2.71875 11.25C3.90625 13.2812 6.03125 14.5 8.375 14.5C10.6875 14.5 12.8125 13.2812 14 11.25C15.1562 9.25 15.1562 6.78125 14 4.75C12.8125 2.75 10.6875 1.5 8.375 1.5ZM8.375 16C5.5 16 2.875 14.5 1.4375 12C0 9.53125 0 6.5 1.4375 4C2.875 1.53125 5.5 0 8.375 0C11.2188 0 13.8438 1.53125 15.2812 4C16.7188 6.5 16.7188 9.53125 15.2812 12C13.8438 14.5 11.2188 16 8.375 16ZM7.125 10.5H7.875V8.5H7.125C6.6875 8.5 6.375 8.1875 6.375 7.75C6.375 7.34375 6.6875 7 7.125 7H8.625C9.03125 7 9.375 7.34375 9.375 7.75V10.5H9.625C10.0312 10.5 10.375 10.8438 10.375 11.25C10.375 11.6875 10.0312 12 9.625 12H7.125C6.6875 12 6.375 11.6875 6.375 11.25C6.375 10.8438 6.6875 10.5 7.125 10.5ZM8.375 6C7.8125 6 7.375 5.5625 7.375 5C7.375 4.46875 7.8125 4 8.375 4C8.90625 4 9.375 4.46875 9.375 5C9.375 5.5625 8.90625 6 8.375 6Z" fill="black"/>
                </svg>
              </button>
            )}

            {/* Upload Button */}
            <Button
              type="button"
              onClick={handleUpload}
              disabled={isUploading || isProcessing || !file}
              aria-busy={isUploading || isProcessing}
              data-testid="fileupload-continue"
              className={`w-full h-[50px] text-white text-lg leading-6 font-medium rounded-full flex items-center justify-center transition-colors ${
                file && !isUploading && !isProcessing
                  ? "bg-[#2D55FB] hover:bg-[#2347E8]"
                  : "bg-[#2D55FB80] cursor-not-allowed"
              }`}
            >
              {isUploading || isProcessing ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  <span>{t.uploading}</span>
                </>
              ) : (
                t.uploadAndContinue
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
